import { QueryResultSelectorResult } from '@reduxjs/toolkit/dist/query/core/buildSelectors';
import { QueryDefinition } from '@reduxjs/toolkit/dist/query/react';
import { AxiosError, isAxiosError } from 'axios';
import { parseAccessToken } from 'lib-shared-auth';
import { NotificationT } from 'lib-shared-legacy-types';
import {
  call,
  debounce,
  put,
  select,
  take,
  takeLatest,
} from 'typed-redux-saga';

import { actions as personalActions } from 'components/settings/actions/personal';
import { featureFlags } from 'utils/featureFlagging/featureFlags';

import { notificationActions } from '../../notifications/actions/notification';
import { actions as conversationActions } from '../actions/conversation';
import {
  articleDraft,
  articleFeedback,
  ArticleFeedbackEvent,
  ArticleTicketAssistEvent,
  articleView,
  ArticleViewEvent,
  ConversationGetSucceededEvent,
  knowledgeGap,
  SearchInputValueUpdatedAction,
  ticketAssist,
  TicketAssistWithArticleEvent,
  ticketNonResolution,
  ticketResolutionExit,
  TicketResolutionExitEvent,
  ticketResolutionStart,
  TicketResolutionStartEvent,
  ticketResolved,
  TicketResolvedEvent,
  ticketView,
} from '../components/integrations/elevio/src/analytics/events';
import {
  SearchArticlesQuery,
  SearchArticlesQueryVariables,
  SuggestionsByTagQuery,
  SuggestionsByTagQueryVariables,
  SuggestionsQuery,
  SuggestionsQueryVariables,
  UserSettingsQuery,
} from '../components/integrations/elevio/src/apis/graphQl/elevio/grappa/types/grappa.types';
import { elevioActions } from '../components/integrations/elevio/src/elevio.actions';
import { elevioSelectors } from '../components/integrations/elevio/src/elevio.reducer/selectors';
import { ElevioCombinedLanguageStore } from '../components/integrations/elevio/src/elevio.reducer/suggestion';
import { ArticleSuggestion } from '../components/integrations/elevio/src/types';
import {
  SetNoSolutionDraftArticleSubmittedPayload,
  SetNoSolutionSubmittedPayload,
  SetResolutionInputValuePayload,
} from '../components/integrations/elevio/src/types/elevio.actions';
import { ConversationThread } from '../components/integrations/elevio/src/types/messages';
import {
  getUrl,
  isValidSearchLength,
} from '../components/integrations/elevio/src/utils';
import { elevioApi } from '../components/integrations/elevio/src/utils/apis/graphql/api';
import { fetchElevioAuthToken } from '../components/integrations/elevio/src/utils/apis/graphql/baseQuery/elevio/elevio';
import { ElevioAccessTokenPayload } from '../components/integrations/elevio/src/utils/apis/graphql/baseQuery/elevio/elevio.types';
import { paging } from '../components/integrations/elevio/src/utils/constants';
import { getDraftTicketThread } from '../components/integrations/elevio/src/utils/messages';
import { transformSuggestions } from '../components/integrations/elevio/src/utils/suggestions';

type GrappaError = { error: { message: string } };

const {
  setInitialConversationSuggestionState,
  viewArticle,
  articleNavBack,
  fetchTokenStart,
  fetchTokenSuccess,
  fetchAvailableLanguagesRequest,
  fetchAvailableLanguagesComplete,
  fetchAvailableLanguagesFailure,
} = elevioActions.suggestions;
const { setSearchInputValue, setSearchTerm, initialiseInputValue } =
  elevioActions.search;
const { ticketAssisted, feedbackSubmit, feedbackReset } =
  elevioActions.feedback;
const {
  debouncedSetSolutionDropdownClosed,
  initiateResolutionPrompt,
  setNoSolutionDraftArticleSubmitted,
  setNoSolutionSubmitted,
  setResolutionInputValue,
  ticketResolutionExited,
  setSolutionDropdownClosed,
  setSolutionSubmitted,
  ticketResolved: ticketResolvedAction,
} = elevioActions.resolution;
const { smartReplyResponseError } = elevioActions.smartReply;

const { notificationAdd } = notificationActions;
const {
  conversationGetSucceeded,
  conversationGetBeforeSucceeded,
  conversationSubmitMessageSucceeded,
  conversationAddWrapup,
  conversationTagSucceeded,
  conversationUntagSucceeded,
} = conversationActions;
const { personalChangedElevioSelectedLanguage } = personalActions;

export function* isElevioSuggestionsEnabled() {
  const isFeatureFlagEnabled = featureFlags.get([
    'elevio-suggestions',
  ]).elevioSuggestions;

  if (!isFeatureFlagEnabled) {
    return false;
  }

  const areUserSettingsValid = yield* call(getUserSettingsAndSetLanguageId); // sets the subscription status as part of its flow

  // Get the subscription status from the store
  const subscriptionStatus = yield* select(
    elevioSelectors.suggestion.subscriptionStatus,
  );

  // If the user settings are valid and the subscription status is active, then we can declare that the feature is enabled
  const isElevioAccountActive =
    areUserSettingsValid && subscriptionStatus === 'Active';

  return isElevioAccountActive;
}

export const elevio = [
  takeLatest(conversationGetSucceeded, handleConversationGetSucceeded),
  takeLatest(
    conversationGetBeforeSucceeded,
    handleConversationGetBeforeSucceeded,
  ),
  debounce(500, setSearchInputValue, debouncedHandleSetSearchInputValue), // Debounced typing
  debounce(
    500,
    setResolutionInputValue,
    debouncedHandleSetResolutionInputValue,
  ), // Debounced typing
  debounce(
    100,
    setSolutionDropdownClosed,
    debouncedHandleSetSolutionDropdownClosed,
  ), // Debounced closing of dropdown

  takeLatest(setSolutionSubmitted, handleSolutionSubmitted),
  takeLatest(setNoSolutionSubmitted, handleNoSolutionSubmitted),
  takeLatest(
    setNoSolutionDraftArticleSubmitted,
    handleNoSolutionDraftArticleSubmitted,
  ),
  takeLatest(ticketResolutionExited, handleTicketResolutionExited),
  takeLatest(ticketResolvedAction, handleTicketResolved),
  takeLatest(initiateResolutionPrompt, handleTicketResolutionStarted),
  takeLatest(feedbackSubmit, handleFeedbackSubmitted),
  takeLatest(ticketAssisted, handleTicketAssist),
  takeLatest(viewArticle, handleViewArticle),
  takeLatest(conversationSubmitMessageSucceeded, handleConversationReply),
  takeLatest(articleNavBack, handleArticleNavBack),
  takeLatest(
    [
      setSearchTerm,
      conversationTagSucceeded,
      conversationUntagSucceeded,
      personalChangedElevioSelectedLanguage,
    ],
    handleConversationChanges,
  ),
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore because `api/executeMutation/fulfilled` does not appear to be exposed in an action anywhere
  takeLatest(['api/executeMutation/fulfilled'], handleTagChanges),
  takeLatest(fetchTokenStart, handleFetchTokenStart),
  takeLatest(smartReplyResponseError, handleSmartReplyError),
];

function* handleConversationGetSucceeded(conversationGetSucceededAction: {
  payload: ConversationGetSucceededEvent;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const languageId = yield* select(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  const resolutionStage = yield* select(elevioSelectors.resolution.stage);
  const resolving = resolutionStage !== 'initial';
  const conversationId = yield* select(elevioSelectors.conversation.id);
  const url = yield* call(getUrl);

  if (conversationId && resolving) {
    // Close resolution if still resolving from another conversation
    yield* put(
      elevioActions.resolution.ticketResolutionExited({
        ticketId: conversationId.toString(),
        url,
        languageId,
      }),
    );
  }

  const status = yield* select(elevioSelectors.conversation.status);
  let companyUid = yield* select(elevioSelectors.suggestion.companyUid);

  if (!companyUid) {
    yield* put(elevioActions.suggestions.fetchTokenStart());
    yield* take(fetchTokenSuccess); // wait for the token to be set before continuing
    companyUid = yield* select(elevioSelectors.suggestion.companyUid);
  }

  if (status && conversationId) {
    yield* call(ticketView, {
      status,
      ticketId: conversationId.toString(),
      url,
      languageId,
    });
  }

  const { csid } = conversationGetSucceededAction.payload.meta;

  const detectedLanguageId = yield* select(
    elevioSelectors.suggestion.getDetectedLanguage(csid),
  );
  if (detectedLanguageId) {
    yield* put(
      elevioActions.suggestions.setDetectedLanguageId({
        conversationId: csid,
        languageId: detectedLanguageId,
      }),
    );
  }

  const allConversationSuggestionState = yield* select(
    elevioSelectors.suggestion.conversationStore,
  );
  const conversationSuggestionState = allConversationSuggestionState[csid];
  if (!conversationSuggestionState) {
    yield* put(setInitialConversationSuggestionState({ conversationId: csid }));
  }

  yield* call(fetchSuggestions);
}

/**
 * Helps with making the search input update in a snappier fashion than if it
 * were updated in handleConversationGetSucceeded
 */
function* handleConversationGetBeforeSucceeded(action: any) {
  const { id: csid } = action.payload.metaConversation.conversation;

  const allConversationSearchState = yield* select(
    elevioSelectors.search.searchStore,
  );
  const conversationSearchState = allConversationSearchState[csid];

  if (!conversationSearchState) {
    yield* put(
      elevioActions.search.setInitialConversationSearchState({
        conversationId: csid,
      }),
    );
  }

  yield* put(
    initialiseInputValue({
      value: conversationSearchState ? conversationSearchState.searchTerm : '',
    }),
  );
}

export function* fetchSuggestions() {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const conversationId = yield* select(elevioSelectors.conversation.id);
  if (!conversationId) return;

  const tagNames = yield* select(elevioSelectors.conversation.tagNames);
  const conversationBody = yield* select(elevioSelectors.conversation.text);

  yield* put(elevioActions.suggestions.fetchSuggestionsRequest());

  let results: ArticleSuggestion[] = [];
  let unableToFindTaggedSuggestions = false;

  const searchTerm = yield* select(elevioSelectors.search.searchTerm);
  const validSearchLength = yield* call(isValidSearchLength, searchTerm);

  // Exit early if no conversation body, no tags and no valid search term
  if (!conversationBody && tagNames.length === 0 && !validSearchLength) {
    yield* put(
      elevioActions.suggestions.fetchSuggestionsComplete({
        conversationId,
        results,
        unableToFindTaggedSuggestions,
      }),
    );
    return;
  }

  // @ts-expect-error because rtk-query is not inferring the correct type
  results = yield* call(callSuggestions, { tagNames });

  if ('error' in results) {
    yield* put(
      elevioActions.suggestions.fetchSuggestionsFailure({
        errorMessage:
          (results as unknown as GrappaError).error.message ||
          'Failed to fetch suggestions',
        ticketId: conversationId,
      }),
    );
    return;
  }

  // If no results, try search again minus tags
  if (
    results.length === 0 &&
    tagNames.length > 0 &&
    !validSearchLength // Don't try to re-fetch suggestions if the search term is too short
  ) {
    unableToFindTaggedSuggestions = true;

    yield* put(elevioActions.suggestions.fetchSuggestionsRequestWithoutTags());
    // @ts-expect-error see above '@ts-expect-error' comment
    results = yield* call(callSuggestions, { tagNames: undefined }); // Re-assign results to the untagged results
    if ('error' in results) {
      yield* put(
        elevioActions.suggestions.fetchSuggestionsFailure({
          errorMessage:
            (results as unknown as GrappaError).error.message ||
            'Failed to fetch suggestions',
          ticketId: conversationId,
        }),
      );
      return;
    }
  }

  yield* put(
    elevioActions.suggestions.setUnableToFindTaggedSuggestions({
      conversationId,
      unableToFindTaggedSuggestions,
    }),
  );

  yield* put(
    elevioActions.suggestions.fetchSuggestionsComplete({
      conversationId,
      results,
      unableToFindTaggedSuggestions,
    }),
  );
}

type GrappaQueryResult<
  TData,
  TLabel extends string,
> = QueryResultSelectorResult<QueryDefinition<unknown, any, TLabel, TData>>;

/**
 * If a searchterm is present, performs a searchArticles query.
 *
 * Otherwise it performs a suggestArticles query using a combination of tags and
 * conversationBody.
 */
export function* callSuggestions({
  tagNames,
}: {
  tagNames: string[] | undefined;
}) {
  const conversationBody = yield* select(elevioSelectors.conversation.text);
  const pageUrl = yield* call(getUrl);
  const searchTerm = yield* select(elevioSelectors.search.searchTerm);
  const languageId = yield* select(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  if (isValidSearchLength(searchTerm)) {
    const searchParams: SearchArticlesQueryVariables = {
      languageId,
      pageUrl,
      query: {
        paging,
        terms: searchTerm,
      },
    };
    const searchPromise = yield* put(
      elevioApi.grappa.endpoints.searchArticles.initiate(searchParams),
    );
    const searchQueryResult: GrappaQueryResult<
      SearchArticlesQuery,
      'searchArticles'
    > = yield searchPromise;

    if ('error' in searchQueryResult) {
      return searchQueryResult;
    }
    if (!searchQueryResult) {
      return {
        error: true,
        name: 'Unknown',
        message: 'Unknown',
      };
    }
    return searchQueryResult.data?.searchArticles.results;
  }

  if (tagNames?.length) {
    const suggestionsByTagParams: SuggestionsByTagQueryVariables = {
      pageUrl,
      languageId,
      tags: tagNames,
      paging,
    };

    const suggestionsByTagPromise = yield* put(
      elevioApi.grappa.endpoints.suggestionsByTags.initiate(
        suggestionsByTagParams,
      ),
    );

    const suggestionsByTagQueryResult: GrappaQueryResult<
      SuggestionsByTagQuery,
      'suggestionsByTags'
    > = yield suggestionsByTagPromise;

    if ('error' in suggestionsByTagQueryResult) {
      return suggestionsByTagQueryResult;
    }

    if (!suggestionsByTagQueryResult) {
      return {
        error: true,
        name: 'Unknown',
        message: 'Unknown',
      };
    }

    return transformSuggestions(suggestionsByTagQueryResult.data);
  }

  const suggestionParams: SuggestionsQueryVariables = {
    languageId,
    pageUrl,
    paging,
    text: conversationBody,
  };
  const suggestionsPromise = yield* put(
    elevioApi.grappa.endpoints.suggestions.initiate(suggestionParams),
  );
  const suggestionsQueryResult: GrappaQueryResult<
    SuggestionsQuery,
    'suggestions'
  > = yield suggestionsPromise;

  if ('error' in suggestionsQueryResult) {
    return suggestionsQueryResult;
  }
  if (!suggestionsQueryResult) {
    return {
      error: true,
      name: 'Unknown',
      message: 'Unknown',
    };
  }
  return transformSuggestions(suggestionsQueryResult.data);
}

function* debouncedHandleSetSearchInputValue(searchInputValueUpdatedAction: {
  payload: SearchInputValueUpdatedAction;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const { value, conversationId } = searchInputValueUpdatedAction.payload;
  yield* put(
    elevioActions.search.setSearchTerm({
      conversationId,
      term: value,
    }),
  );
}

function* debouncedHandleSetResolutionInputValue(setResolutionInputValueAction: {
  payload: SetResolutionInputValuePayload;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const { resolutionInputValue } = setResolutionInputValueAction.payload;
  yield* put(
    elevioActions.resolution.setResolutionSearchTerm({
      resolutionSearchTerm: resolutionInputValue,
    }),
  );
}

/**
 * A debounce is required to delay the search input from closing its dropdown to
 * allow enough time between the onBlur event and the onClick event of the
 * popup. Without a timeout, the popup is dismissed before your click can be
 * registered to an article in the dropdown, therefore no article will be
 * selected.
 */
function* debouncedHandleSetSolutionDropdownClosed() {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  yield* put(debouncedSetSolutionDropdownClosed());
}

type Wrapup = {
  csid: string;
  note: string;
  agentId: string;
};
type NotificationOptions = Partial<NotificationT> & {
  time: number;
};
function* handleSolutionSubmitted(solutionSubmittedAction: {
  payload: TicketAssistWithArticleEvent;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const {
    helpfulArticleId,
    suggestedArticleIds,
    ticketId,
    ticketSubject,
    ticketTags,
    ticketThread,
    url,
    articleTitle,
    languageId,
  } = solutionSubmittedAction.payload;

  const options: NotificationOptions = {
    title: 'Success',
    text: `The article "${articleTitle}" has been marked as resolving this issue`,
    icon: 'done',
    className: 'success',
    time: 5000,
  };
  yield* put(notificationAdd(options));

  yield* call(ticketResolved, {
    helpfulArticleId,
    suggestedArticleIds,
    ticketId,
    ticketSubject,
    ticketTags,
    ticketThread,
    url,
    languageId,
  });

  const conversationId = yield* select(elevioSelectors.conversation.id);
  const agentId = yield* select(elevioSelectors.conversation.agentId);
  if (!conversationId || !agentId) return;

  const internalNote: Wrapup = {
    csid: conversationId.toString(),
    note: `An article was used to resolve this conversation: ${articleTitle} [${helpfulArticleId}]`,
    agentId,
  };
  yield* put(conversationAddWrapup(internalNote));
}

function* handleNoSolutionSubmitted(noSolutionSubmittedAction: {
  payload: SetNoSolutionSubmittedPayload;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const {
    suggestedArticleIds,
    ticketId,
    ticketTags,
    url,
    ticketSubject,
    ticketThread,
  } = noSolutionSubmittedAction.payload;

  const options: NotificationOptions = {
    title: 'Alert',
    text: `Conversation has been resolved without a solution.`,
    icon: 'done',
    className: 'warning',
    time: 5000,
  };
  yield* put(notificationAdd(options));

  const languageId = yield* select(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  yield* call(ticketNonResolution, {
    suggestedArticleIds,
    ticketId,
    ticketSubject,
    ticketTags,
    ticketThread,
    url,
    languageId,
  });

  yield* call(knowledgeGap, {
    suggestedArticleIds,
    ticketId,
    ticketTags,
    url,
    languageId,
  });
}

function* handleNoSolutionDraftArticleSubmitted(noSolutionDraftArticleSubmittedAction: {
  payload: SetNoSolutionDraftArticleSubmittedPayload;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const {
    ticketId,
    ticketThread,
    ticketTitle,
    url,
    suggestedArticleIds,
    ticketSubject,
    ticketTags,
    notes,
  } = noSolutionDraftArticleSubmittedAction.payload;

  const options: NotificationOptions = {
    title: 'Alert',
    text: `Draft article "${ticketTitle}" has been created.`,
    icon: 'done',
    className: 'warning',
    time: 5000,
  };
  yield* put(notificationAdd(options));

  const languageId = yield* select(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  yield* call(ticketNonResolution, {
    suggestedArticleIds,
    ticketId,
    ticketSubject,
    ticketTags,
    ticketThread,
    url,
    languageId,
  });

  const draft = yield* select(elevioSelectors.resolution.draft);
  if (draft) {
    const parsedContent = removeWrappingDivFromArticleContent(
      draft.articleContent,
    );
    const draftTicketThread: ConversationThread = yield* call(
      getDraftTicketThread,
      {
        tagNames: ticketTags,
        url,
        notes,
        articleContent: parsedContent,
      },
    );
    yield* call(articleDraft, {
      ticketId,
      ticketTitle,
      ticketThread: draftTicketThread,
      url,
      languageId,
    });
  }
}

/**
 * Habla RichText Editor may return content wrapped in an incompatible <div
 * dir="ltr" />.
 *
 * This function removes the wrapper from the html string if necessary.
 */
export function removeWrappingDivFromArticleContent(
  articleContent: string,
): string {
  const htmlDom = new DOMParser().parseFromString(articleContent, 'text/html');
  const domBody = htmlDom.body;
  const childNodes = domBody.childNodes;

  if (
    childNodes.length === 0 || // Means no childnodes, bail and return articleContent
    childNodes.length > 1 // Multiple children means no div wrapper, just return the content as is without parsing
  ) {
    return articleContent;
  }

  // Parsed child nodes
  const parsedChildNodes = childNodes[0].childNodes;
  const parsedChildNodesAsHtmlString = Array.from(parsedChildNodes).reduce(
    (accumulator, node) => {
      const tempElement = document.createElement('div');
      tempElement.appendChild(node);
      const innerHtml = tempElement.innerHTML;
      return accumulator.concat(innerHtml);
    },
    '',
  );
  return parsedChildNodesAsHtmlString;
}

function* handleTicketResolutionExited(ticketResolutionExitedAction: {
  payload: TicketResolutionExitEvent;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const { ticketId, url, languageId } = ticketResolutionExitedAction.payload;
  yield* call(ticketResolutionExit, {
    ticketId,
    url,
    languageId,
  });
}

function* handleTicketResolved(ticketResolvedAction: {
  payload: TicketResolvedEvent;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const {
    ticketId,
    url,
    helpfulArticleId,
    suggestedArticleIds,
    ticketSubject,
    ticketTags,
    ticketThread,
    languageId,
  } = ticketResolvedAction.payload;
  yield* call(ticketResolved, {
    helpfulArticleId,
    suggestedArticleIds,
    ticketId,
    ticketSubject,
    ticketTags,
    ticketThread,
    url,
    languageId,
  });
  yield* put(
    initiateResolutionPrompt({
      ticketId,
      url,
      languageId,
    }),
  );
}

function* handleTicketResolutionStarted(ticketResolutionStartedAction: {
  payload: TicketResolutionStartEvent;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const { ticketId, url, languageId } = ticketResolutionStartedAction.payload;
  yield* call(ticketResolutionStart, {
    ticketId,
    url,
    languageId,
  });
}

function* handleFeedbackSubmitted(feedbackSubmittedAction: {
  payload: ArticleFeedbackEvent;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const {
    articleId,
    articleLastPublishedAt,
    feedbackText,
    ticketId,
    url,
    languageId,
  } = feedbackSubmittedAction.payload;

  yield* call(articleFeedback, {
    articleId,
    articleLastPublishedAt,
    feedbackText,
    ticketId,
    url,
    languageId,
  });

  const options: NotificationOptions = {
    title: 'Success',
    text: `Feedback submitted`,
    icon: 'done',
    className: 'success',
    time: 5000,
  };
  yield* put(notificationAdd(options));

  yield* put(feedbackReset());
}

function* handleTicketAssist(ticketAssistAction: {
  payload: ArticleTicketAssistEvent;
}) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const {
    articleId,
    articleTags,
    suggestedArticleIds,
    ticketId,
    ticketSubject,
    ticketThread,
    url,
  } = ticketAssistAction.payload;

  const languageId = yield* select(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  yield* call(ticketAssist, {
    assistType: 'helpful_click',
    helpfulArticleIds: [articleId],
    suggestedArticleIds,
    ticketId: ticketId.toString(),
    ticketSubject,
    ticketTags: articleTags,
    ticketThread,
    url,
    languageId,
  });

  const options: NotificationOptions = {
    title: 'Success',
    text: `Feedback submitted`,
    icon: 'done',
    className: 'success',
    time: 5000,
  };
  yield* put(notificationAdd(options));
}

function* handleViewArticle(viewArticleAction: { payload: ArticleViewEvent }) {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  if (!viewArticleAction?.payload?.articleId) return; // Middleware registration runs this function on initial load, which is not desirable. Bail until function is run with a valid articleId in the payload.

  const { articleId, ticketStatus, ticketTags, url, ticketId, languageId } =
    viewArticleAction.payload;

  yield* call(articleView, {
    articleId,
    ticketStatus,
    ticketTags,
    url,
    ticketId,
    languageId,
  });
}

function* handleConversationReply() {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  const { data } = yield* select(elevioSelectors.user.kcsSettings);
  const kcsSettingsMode = data?.kcsSettings?.mode;
  const conversationId = yield* select(elevioSelectors.conversation.id);
  const languageId = yield* select(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  if (conversationId && kcsSettingsMode === 'REPLY') {
    const ticketId: string = conversationId.toString();
    yield* put(
      initiateResolutionPrompt({
        ticketId,
        url: yield* call(getUrl),
        languageId,
      }),
    );
  }
}

function* handleArticleNavBack() {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  yield* put(elevioActions.feedback.feedbackReset());
}

function* handleConversationChanges() {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  yield* call(fetchSuggestions);
}

/** There seems to be no way to find a standard type for this payload */
type HandleTagChangesPayload = {
  meta: {
    arg: {
      endpointName: string;
    };
  };
};
function* handleTagChanges(payload: HandleTagChangesPayload) {
  if (payload.meta.arg.endpointName !== 'updateConversationTag') return; // Bail early if it's not a tag update

  const conversationId = yield* select(elevioSelectors.conversation.id);
  if (!conversationId) return null;

  yield* call(fetchSuggestions);
}

function* handleFetchTokenStart() {
  const suggestionsEnabled = yield* call(isElevioSuggestionsEnabled);
  if (!suggestionsEnabled) return;

  try {
    const result = yield* call(fetchElevioAuthToken);
    const parsedToken = parseAccessToken(
      result.elevioToken,
    ) as unknown as ElevioAccessTokenPayload;
    const companyUid = parsedToken.sub;
    const userId = parsedToken.userId;
    const userEmail = parsedToken.userName;
    yield* put(
      elevioActions.suggestions.fetchTokenSuccess({
        elevioAuthStatus: 'success',
        token: result.elevioToken,
        companyUid,
        userId,
        userEmail,
      }),
    );
  } catch (e: unknown) {
    yield* put(elevioActions.suggestions.fetchTokenError());

    const isError = isAxiosError(e);
    if (isError) {
      const axiosError = e as AxiosError;
      return {
        error: {
          message: axiosError.message,
          status: axiosError.response?.status,
        },
      };
    }
    return {
      error: e,
    };
  }
}

function* getUserSettingsAndSetLanguageId() {
  // check if languages in the store yet
  const derivedStore: ElevioCombinedLanguageStore = yield* select(
    elevioSelectors.suggestion.languages,
  );
  if (derivedStore.availableLanguages.results) return true;

  yield* put(fetchAvailableLanguagesRequest());
  const userSettingsPromise = yield* put(
    elevioApi.grappa.endpoints.userSettings.initiate({
      pageUrl: window.location.origin,
    }),
  );
  const userSettingsQueryResult: GrappaQueryResult<
    UserSettingsQuery,
    'userSettings'
  > = yield userSettingsPromise;

  if (
    'error' in userSettingsQueryResult ||
    !userSettingsQueryResult ||
    !userSettingsQueryResult.data
  ) {
    yield* put(
      fetchAvailableLanguagesFailure({
        errorMessage: 'Error fetching available languages',
      }),
    );
    return false;
  }

  yield* put(
    fetchAvailableLanguagesComplete({
      languages: userSettingsQueryResult.data.userSettings.company.languages,
      defaultLanguageId:
        userSettingsQueryResult.data.userSettings.company.defaultLanguage.id,
    }),
  );

  const languages = yield* select(elevioSelectors.suggestion.languages);
  const { selectedLanguageMode } = languages;
  if (
    selectedLanguageMode === 'autoDetectedLanguage' ||
    selectedLanguageMode === 'defaultLanguage'
  ) {
    yield* put(
      personalActions.personalChangedElevioSelectedLanguage({
        elevioSelectedLanguage: selectedLanguageMode,
      }),
    );
  }

  return true;
}

function* handleSmartReplyError() {
  const options: NotificationOptions = {
    text: `Oh no! It seems like our AI assistant is having an uncharacteristic hiccup. Please wait a while and try again later.`,
    title: 'Error',
    showDesktop: true,
    icon: 'exclamation',
    status: 'danger',
    time: 5000,
  };
  yield* put(notificationAdd(options));
}
